import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSearch, faShop } from "@fortawesome/pro-light-svg-icons"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import {
    SUPPORT_ENTITIES_COMPANY_ROUTE,
    SUPPORT_ENTITIES_CREATE_COMPANY_ROUTE,
    SUPPORT_ENTITIES_CREATE_SUPPLIER_ROUTE,
    SUPPORT_ENTITIES_SUPPLIER_ROUTE,
    SUPPORT_REPORTS_ROUTE,
} from "../../../../../constants/routes"
import React, { useCallback, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { MRT_ColumnDef, MRT_Row, MRT_GlobalFilterTextField } from "material-react-table"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { deleteEntity, fetchEntities, FetchSupportEntityResponse, SupportEntityQuery } from "../supportEntityApi"
import { Entity, EntityStatus, EntityType, EntityTypeFilter } from "../supportEntitiesTypes"
import { EntityDetails } from "../components/EntityDetails"
import { Chip, FormControlLabel, InputAdornment, MenuItem, Switch, Box } from "@mui/material"
import { tailwindPalette } from "../../../../theme/tailwindPalette"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { EntityTableNameCell } from "../components/EntityTableNameCell"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../core/api/errorTypes"
import { SelectEntityType } from "../components/SelectEntityType"
import { pushContext } from "../../../../context/contextSlice"
import { useAppDispatch } from "../../../../store/storeHooks"
import { buildContextData, entityContextMap } from "../helpers"
import { SplitButton } from "../../../../../shared/components/buttons/SplitButton"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"

type Props = Readonly<{
    readonly?: boolean
}>

export const options = [
    EntityType.SUPPLIER,
    // EntityType.MERCHANT
    EntityType.COMPANY,
    // EntityType.TENANT
]

function transformData(data: FetchSupportEntityResponse) {
    return data.entities
}

export function SupportEntities({ readonly = false }: Props) {
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const { l10n } = useAppLocalization()

    const dispatch = useAppDispatch()

    const [isActive, setIsActive] = useState(false)
    const [globalFilter, setGlobalFilter] = useState("")
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [entity, setEntity] = useState<Entity>()
    const [entityType, setEntityType] = useState<EntityTypeFilter>(EntityTypeFilter.ALL)

    const supportEntities = useApiQuery(fetchEntities, {
        queryName: SupportEntityQuery.LIST,
        dependencies: {
            entityType,
            isActive: !isActive,
        },
        select: transformData,
    })

    const { mutate: callDeleteEntity } = useApiMutation(deleteEntity, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [SupportEntityQuery.LIST] })
        },
    })

    function handleRowClick({ original }: MRT_Row<Entity>) {
        const entityType = original.type
        const contextData = buildContextData(entityType, original, pathname)

        dispatch(pushContext(contextData))
        navigate(entityContextMap[entityType].route)
    }

    const { ApiQueryTable, columnFactory } = useApiQueryTable(supportEntities)

    const columns = useMemo<MRT_ColumnDef<Entity>[]>(
        () => [
            columnFactory.typeColumn({
                header: l10n.getString("support-entity-table-header-id"),
                accessorKey: "id",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("support-entity-table-header-name"),
                accessorKey: "name",
                Cell: ({ row }) => <EntityTableNameCell onNameClick={() => handleRowClick(row)} row={row} />,
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("support-entity-table-header-address"),
                accessorKey: "address.displayName",
            }),
            columnFactory.typeColumn({
                header: l10n.getString("support-entity-table-header-type"),
                accessorKey: "type",
                Cell: ({ row }) => {
                    return <EntityTypeChip type={row.original?.type} />
                },
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("support-entity-table-header-status"),
                },
                (row) => row?.status === EntityStatus.ACTIVE,
            ),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<Entity>; closeMenu: () => void }) => {
            const menuItems = [
                <MenuItem
                    key={`details-${row.original.id}`}
                    onClick={() => {
                        navigate(`/details/${row.original.type}`)
                        closeMenu()
                    }}
                >
                    <LocalizedStrict id="support-entity-table-button-details">
                        <span>Details</span>
                    </LocalizedStrict>
                </MenuItem>,
            ]

            if (!readonly) {
                menuItems.push(
                    <MenuItem
                        key={`edit-${row.original.id}`}
                        onClick={() => {
                            onEditClick(row.original)
                            closeMenu()
                        }}
                    >
                        <LocalizedStrict id="support-entity-table-button-edit">
                            <span>Edit</span>
                        </LocalizedStrict>
                    </MenuItem>,
                    <MenuItem
                        key={`delete-${row.original.id}`}
                        onClick={() => {
                            setEntity(row.original)
                            setShowConfirmModal(true)
                            closeMenu()
                        }}
                    >
                        <LocalizedStrict id="support-entity-table-button-delete">
                            <span>Delete</span>
                        </LocalizedStrict>
                    </MenuItem>,
                )
            }

            return menuItems
        },
        [readonly],
    )

    function onCreateClick(actionKey: EntityType | string) {
        switch (actionKey) {
            case EntityType.COMPANY:
                navigate(SUPPORT_ENTITIES_CREATE_COMPANY_ROUTE)
                break
            case EntityType.SUPPLIER:
                navigate(SUPPORT_ENTITIES_CREATE_SUPPLIER_ROUTE)
                break
            default:
                break
        }
    }

    function onEditClick(entity: Entity) {
        switch (entity.type) {
            case EntityType.COMPANY:
                navigate(`${SUPPORT_ENTITIES_COMPANY_ROUTE}/${entity.id}`)
                break
            case EntityType.SUPPLIER:
                navigate(`${SUPPORT_ENTITIES_SUPPLIER_ROUTE}/${entity.id}`)
                break
            default:
                break
        }
    }

    return (
        <>
            <LocalizedStrict id="support-entity-header-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPORT_ENTITIES}
                    title="Entities"
                    icon={<FontAwesomeIcon icon={faShop} />}
                    rightElement={
                        readonly ? undefined : (
                            <SplitButton
                                options={options}
                                optionBase="support-entity-header-menu-button-create"
                                onSelect={onCreateClick}
                                buttonLabel="Create"
                            />
                        )
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="support-entity-header-nav-item-home">
                            <BreadcrumbLink to={SUPPORT_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-entity-header-nav-item-entities">
                            <Breadcrumb>Entities</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious", showGlobalFilter: true }}
                renderDetailPanel={({ row }) => <EntityDetails row={row} />}
                renderRowActionMenuItems={renderRowActionMenuItems}
                muiSearchTextFieldProps={{
                    variant: "outlined",
                    InputProps: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <FontAwesomeIcon icon={faSearch} />
                            </InputAdornment>
                        ),
                    },
                }}
                positionGlobalFilter="none"
                state={{ globalFilter }}
                onGlobalFilterChange={setGlobalFilter}
                renderTopToolbarCustomActions={({ table }) => (
                    <Box p="1rem" gap={2} display="flex">
                        <SelectEntityType entityType={entityType} setEntityType={setEntityType} />
                        <MRT_GlobalFilterTextField table={table} />
                    </Box>
                )}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" showGlobalFilterButton={false} table={table}>
                        <LocalizedStrict id="support-entity-table-show-inactive" attrs={{ label: true }}>
                            <FormControlLabel
                                control={<Switch checked={isActive} onChange={() => setIsActive((prev) => !prev)} />}
                                label="Show inactive"
                                labelPlacement="start"
                            />
                        </LocalizedStrict>
                    </StandardTableFilter>
                )}
            />

            {/* Confirm delete */}
            <LocalizedStrict id="support-entity-confirm-delete-entity-dialog" attrs={{ typeOfEntity: true }}>
                <DeleteConfirmDialog
                    typeOfEntity="entity"
                    entityName={entity?.name || ""}
                    handleClick={() => {
                        if (entity?.id) {
                            callDeleteEntity({ entityId: entity?.id })
                        } else {
                            throw new Error("Entity id is undefined")
                        }
                    }}
                    onClose={() => setShowConfirmModal(false)}
                    open={showConfirmModal}
                />
            </LocalizedStrict>
        </>
    )
}

function EntityTypeChip({ type }: { type: EntityType }) {
    switch (type) {
        case EntityType.SUPPLIER:
            return (
                <LocalizedStrict id="support-entity-table-type-supplier" attrs={{ label: true }}>
                    <Chip
                        label="Supplier"
                        sx={{
                            borderRadius: ".5rem",
                            fontWeight: 500,
                            border: `1px solid ${tailwindPalette.sky[300]}`,
                            backgroundColor: tailwindPalette.sky[50],
                            color: tailwindPalette.sky[700],
                        }}
                    />
                </LocalizedStrict>
            )
        case EntityType.MERCHANT:
            return (
                <LocalizedStrict id="support-entity-table-type-merchant" attrs={{ label: true }}>
                    <Chip
                        label="Merchant"
                        sx={{
                            borderRadius: ".5rem",
                            fontWeight: 500,
                            border: `1px solid ${tailwindPalette.sky[300]}`,
                            backgroundColor: tailwindPalette.sky[50],
                            color: tailwindPalette.sky[700],
                        }}
                    />
                </LocalizedStrict>
            )
        case EntityType.COMPANY:
            return (
                <LocalizedStrict id="support-entity-table-type-company" attrs={{ label: true }}>
                    <Chip
                        label="Company"
                        sx={{
                            borderRadius: ".5rem",
                            fontWeight: 500,
                            border: `1px solid ${tailwindPalette.sky[300]}`,
                            backgroundColor: tailwindPalette.sky[50],
                            color: tailwindPalette.sky[700],
                        }}
                    />
                </LocalizedStrict>
            )
        case EntityType.TENANT:
            return (
                <LocalizedStrict id="support-entity-table-type-tenant" attrs={{ label: true }}>
                    <Chip
                        label="Tenant"
                        sx={{
                            borderRadius: ".5rem",
                            fontWeight: 500,
                            border: `1px solid ${tailwindPalette.sky[300]}`,
                            backgroundColor: tailwindPalette.sky[50],
                            color: tailwindPalette.sky[700],
                        }}
                    />
                </LocalizedStrict>
            )
        case EntityType.SERVICE_CENTER:
            return (
                <LocalizedStrict id="support-entity-table-type-service-center" attrs={{ label: true }}>
                    <Chip
                        label="Service Center"
                        sx={{
                            borderRadius: ".5rem",
                            fontWeight: 500,
                            border: `1px solid ${tailwindPalette.sky[300]}`,
                            backgroundColor: tailwindPalette.sky[50],
                            color: tailwindPalette.sky[700],
                        }}
                    />
                </LocalizedStrict>
            )
        case EntityType.USER:
            return (
                <LocalizedStrict id="support-entity-table-type-user" attrs={{ label: true }}>
                    <Chip
                        label="User"
                        sx={{
                            borderRadius: ".5rem",
                            fontWeight: 500,
                            border: `1px solid ${tailwindPalette.sky[300]}`,
                            backgroundColor: tailwindPalette.sky[50],
                            color: tailwindPalette.sky[700],
                        }}
                    />
                </LocalizedStrict>
            )
    }
}
